import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import { PageHero, CTABand, AreaChips } from "../components/ui-kit";
import { Reveal } from "../motion";
import { AREAS } from "../content";

function FAQ({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="divide-y divide-ink/12 border-y border-ink/12">
      {items.map((f, i) => (
        <div key={i}>
          <button data-testid={`faq-${i}`} onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center justify-between gap-6 py-6 text-left group">
            <span className="serif text-xl lg:text-2xl text-ink group-hover:text-gold transition-colors">{f.q}</span>
            <motion.span animate={{ rotate: open === i ? 45 : 0 }} className="text-gold shrink-0"><Plus size={22} /></motion.span>
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                <p className="pb-6 text-ink/65 leading-relaxed font-light max-w-3xl">{f.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}

export default function PracticeArea() {
  const { slug } = useParams();
  const area = AREAS[slug];
  if (!area) return <Navigate to="/404" replace />;

  const titleWords = area.title.split(" ");
  const mid = Math.ceil(titleWords.length / 2);
  const titleLines = [titleWords.slice(0, mid).join(" "), titleWords.slice(mid).join(" ")].filter(Boolean);

  return (
    <>
      <PageHero eyebrow={area.kicker} titleLines={titleLines} lead={area.lead} index={area.n} />
      <section className="bg-bone py-20 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1.6fr_1fr] gap-14 lg:gap-20">
            <div className="space-y-14">
              {area.sections.map((s, i) => (
                <Reveal key={i} className="border-t border-ink/12 pt-8">
                  <h2 className="serif text-2xl lg:text-4xl text-ink mb-5 max-w-2xl">{s.h}</h2>
                  <p className="text-ink/70 text-lg leading-relaxed font-light max-w-3xl">{s.p}</p>
                </Reveal>
              ))}
            </div>
            <div className="lg:sticky lg:top-28 h-fit">
              <Reveal className="bg-ink text-bone rounded-2xl p-8 grain overflow-hidden">
                <div className="eyebrow text-gold mb-4">Apresente o contexto</div>
                <p className="text-bone/70 font-light leading-relaxed text-[0.95rem] mb-7">
                  Uma descrição inicial do assunto, das pessoas ou empresas envolvidas e de eventual prazo permite avaliar o direcionamento e as informações necessárias.
                </p>
                <Link to="/contato" data-testid="area-cta" className="btn-gold w-full justify-center">{area.cta} <ArrowRight size={16} /></Link>
              </Reveal>
            </div>
          </div>

          {area.faq && area.faq.length > 0 && (
            <div className="mt-24">
              <Reveal><div className="eyebrow text-gold mb-6">Perguntas frequentes</div></Reveal>
              <Reveal delay={0.1}><FAQ items={area.faq} /></Reveal>
            </div>
          )}

          <div className="mt-24 border-t border-ink/12 pt-10">
            <div className="eyebrow text-ink/40 mb-6">Áreas relacionadas</div>
            <AreaChips slugs={area.related} areas={AREAS} />
          </div>
        </div>
      </section>
      <CTABand title={["Fale com o", "escritório."]} text="Documentos sensíveis podem ser encaminhados após a confirmação do atendimento e do canal adequado para seu recebimento." primaryLabel={area.cta} />
    </>
  );
}
